import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { toCSV } from "./overviewExportCommon";

const BUTTON_THEMES = {
  amber: {
    dark: "border-gray-600 bg-gray-900/70 text-gray-100 hover:bg-gray-800",
    light: "border-amber-200 bg-white text-amber-900 hover:bg-amber-50",
  },
  sky: {
    dark: "border-gray-600 bg-gray-900/70 text-gray-100 hover:bg-gray-800",
    light: "border-sky-200 bg-white text-sky-900 hover:bg-sky-50",
  },
};

export default function OverviewExportMenu({
  data,
  isDarkMode = false,
  disabled = false,
  buildSheets,
  buildSingleCsvRows,
  buildFileBase,
  i18nPrefix = "export_overview",
  theme = "amber",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const fileBase = useMemo(() => buildFileBase(data?.selectedDate), [buildFileBase, data?.selectedDate]);
  const sheets = useMemo(() => buildSheets(data), [buildSheets, data]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    Object.entries(sheets).forEach(([name, rows]) => {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), name.slice(0, 31));
    });
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `${fileBase}.xlsx`);
    setOpen(false);
  };

  const handleExportCSVZip = async () => {
    const zip = new JSZip();
    Object.entries(sheets).forEach(([name, rows]) => {
      zip.file(`${name}.csv`, toCSV(rows));
    });
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `${fileBase}.zip`);
    setOpen(false);
  };

  const handleExportCSVSingle = () => {
    const rows = buildSingleCsvRows(data);
    const csv = toCSV(rows);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${fileBase}_all.csv`);
    setOpen(false);
  };

  const menuItemClass = [
    "w-full text-left px-3 py-2 text-sm rounded-md",
    isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50",
  ].join(" ");

  const buttonTheme = BUTTON_THEMES[theme] || BUTTON_THEMES.amber;

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        title={
          disabled
            ? t(`${i18nPrefix}.disabled_no_data`, { defaultValue: "Aucune donnée à exporter" })
            : t(`${i18nPrefix}.export`, { defaultValue: "Exporter" })
        }
        className={[
          "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40",
          isDarkMode ? buttonTheme.dark : buttonTheme.light,
        ].join(" ")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download className="h-4 w-4 shrink-0" />
        <span>{t(`${i18nPrefix}.export`, { defaultValue: "Exporter" })}</span>
        <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {open && !disabled ? (
        <div
          role="menu"
          className={[
            "absolute right-0 z-50 mt-2 w-56 rounded-md border p-2 shadow-lg",
            isDarkMode ? "border-gray-700 bg-gray-900 text-gray-100 shadow-black/30" : "border-gray-200 bg-white text-gray-800",
          ].join(" ")}
        >
          <button type="button" role="menuitem" onClick={handleExportExcel} className={menuItemClass}>
            {t(`${i18nPrefix}.option_excel`, { defaultValue: "Excel (.xlsx)" })}
          </button>
          <button type="button" role="menuitem" onClick={handleExportCSVZip} className={menuItemClass}>
            {t(`${i18nPrefix}.option_csv_zip`, { defaultValue: "CSV – plusieurs fichiers (.zip)" })}
          </button>
          <button type="button" role="menuitem" onClick={handleExportCSVSingle} className={menuItemClass}>
            {t(`${i18nPrefix}.option_csv_single`, { defaultValue: "CSV – fichier unique" })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
